import React, {
  ChangeEvent, FC, useCallback, useMemo,
} from 'react';
import {
  createMuiTheme,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButtonProps,
  Popover,
  Switch,
  ThemeProvider,
} from '@material-ui/core';
import { DEFAULT_THEME_COLOR } from 'appenv';
import styled from 'styled-components';
import { NotificationsRounded } from '@material-ui/icons';
import {
  selectNotificationSettings,
  setNotificationSetting,
  SetNotificationPayload,
  ConversationType,
} from 'models/twilio';
import { useDispatch, useSelector } from 'react-redux';
import {
  usePopupState,
  bindTrigger,
  bindPopover,
} from 'material-ui-popup-state/hooks';
import { useTranslation } from 'react-i18next';
import TRANSLATIONS from 'components/twilio/translations';
import ColoredMaterialIcon from './styled/ColoredMaterialIcon';
import ChatIconButton from './styled/ChatIconButton';
import ChatDivider from './styled/ChatDivider';
import useFirebaseChatConfig from '../hooks/useFirebaseChatConfig';

const ChatNotificationTitle = styled.div`
  font-family: Lato, san-serif;
  font-size: 14px;
  font-weight: bold;
  padding: 12px 16px;
`;

const StyledFormGroup = styled(FormGroup)`
  padding: 8px;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin: 2px 0 2px 8px;
  justify-content: space-between;
  & .ChatNotificationSettingLabel {
    font-size: 14px;
    font-family: Lato, san-serif;
    margin-right: 16px;
  }
`;

interface SwitchInterface {
  checked: boolean;
  label: string;
  name: ConversationType,
}

const ChatNotificationMenu: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { boothChatroomDisabled, publicChatrooms } = useFirebaseChatConfig();
  const notificationSettings = useSelector(selectNotificationSettings);
  const { themeColor = DEFAULT_THEME_COLOR } = useSelector((s) => s.eventConfigs);

  const theme = useMemo(() => createMuiTheme({
    palette: {
      primary: {
        main: themeColor,
      },
    },
  }), [themeColor]);

  const filterMap = useMemo((): Record<ConversationType, boolean> => ({
    [ConversationType.ANNOUNCEMENT]: true,
    [ConversationType.PRIVATE]: true,
    [ConversationType.GROUP]: !boothChatroomDisabled,
    [ConversationType.PUBLIC]: Boolean(publicChatrooms?.length || 0),
    [ConversationType.UNKNOWN]: false,
  }), [boothChatroomDisabled, publicChatrooms]);

  const switchParameters: SwitchInterface[] = useMemo(() => {
    const switchInterface = [
      {
        checked: notificationSettings[ConversationType.ANNOUNCEMENT],
        label: t(...TRANSLATIONS.JOINED_ROOMS.ANNOUNCEMENT),
        name: ConversationType.ANNOUNCEMENT,
      },
      {
        checked: notificationSettings[ConversationType.PRIVATE],
        label: t(...TRANSLATIONS.JOINED_ROOMS.PRIVATE),
        name: ConversationType.PRIVATE,
      },
      {
        checked: notificationSettings[ConversationType.GROUP],
        label: t(...TRANSLATIONS.JOINED_ROOMS.GROUP),
        name: ConversationType.GROUP,
      },
      {
        checked: notificationSettings[ConversationType.PUBLIC],
        label: t(...TRANSLATIONS.JOINED_ROOMS.PUBLIC),
        name: ConversationType.PUBLIC,
      },
    ];
    return switchInterface.filter((param) => filterMap[param.name]);
  }, [notificationSettings, filterMap, t]);

  const TitleComponent = useMemo(() => <ChatNotificationTitle>{t(...TRANSLATIONS.COMMON.NOTIFICATION)}</ChatNotificationTitle>, [t]);

  const onSwitchToggle = useCallback((e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const payload = {
      settingTarget: e.target.name,
      switchValue: checked,
    } as SetNotificationPayload;
    dispatch(setNotificationSetting(payload));
  }, [dispatch]);

  return (
    <ThemeProvider theme={theme}>
      {TitleComponent}
      <ChatDivider />
      <FormControl component="fieldset">
        <StyledFormGroup>
          {switchParameters.map(({ checked, label, name }) => (
            <StyledFormControlLabel
              key={`SoundSwitch-${name}`}
              classes={{ label: 'ChatNotificationSettingLabel' }}
              control={<Switch name={name} color="primary" checked={checked} onChange={onSwitchToggle} />}
              label={label}
              labelPlacement="start"
            />
          ))}
        </StyledFormGroup>
      </FormControl>
    </ThemeProvider>
  );
};

type BindTriggerProvidedProps = keyof ReturnType<typeof bindTrigger>;
type MobileChatButtonProps = Omit<IconButtonProps, BindTriggerProvidedProps> & { iconColor: string };

const ChatBellButton: FC<MobileChatButtonProps> = ({ iconColor, ...buttonProps }) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: `ChatBellButton-${iconColor}`,
  });
  return (
    <>
      <ChatIconButton {...buttonProps} {...bindTrigger(popupState)}>
        <ColoredMaterialIcon Icon={NotificationsRounded} color={iconColor} />
      </ChatIconButton>
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <ChatNotificationMenu />
      </Popover>
    </>
  );
};

export default ChatBellButton;
